// JoinFamily — Jul 2026 (Randy, Phase B/C).
// Landing page for shareable family invite links (/join-family/:token).
// Logged-in users join directly; new users are sent to signup/login with the
// token parked in localStorage — InvitesBanner redeems it right after auth.
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarHeart, ShieldWarning, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getUser } from "@/lib/api";

export default function JoinFamily() {
  const { token } = useParams();
  const nav = useNavigate();
  const authed = !!getUser();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/join-family/${token}`);
        setInfo(r.data);
      } catch {
        setInfo({ valid: false });
      } finally { setLoading(false); }
    })();
  }, [token]);

  const join = async () => {
    setJoining(true);
    try {
      const r = await api.post(`/join-family/${token}/redeem`);
      toast.success(`Welcome to “${r.data.silo_name}” — opening your family view.`);
      nav(`/family-view/${r.data.silo_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not join — the link may have been revoked.");
      setJoining(false);
    }
  };

  const goAuth = (path) => {
    try { localStorage.setItem("mysilo_family_token", token); } catch { /* */ }
    nav(path);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center text-sm text-[#6B6A68]" data-testid="join-family-loading">
        <Spinner size={16} className="animate-spin mr-2" /> Checking your invite…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center px-6" data-testid="join-family-page">
      <div className="w-full max-w-md bg-white border hairline rounded-xl p-8 shadow-sm">
        {!info?.valid ? (
          <div data-testid="join-family-invalid">
            <h1 className="font-serif-display text-2xl text-[#1C1B1A]">This invite link isn't active</h1>
            <p className="text-sm text-[#6B6A68] mt-2 leading-relaxed">
              It may have been revoked or mistyped. Ask the person who sent it to share a fresh link.
            </p>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full bg-[#4A5D4E] text-[#FDFCF8] flex items-center justify-center mb-4">
              <CalendarHeart size={24} weight="duotone" />
            </div>
            <h1 className="font-serif-display text-2xl text-[#1C1B1A]" data-testid="join-family-title">
              {info.inviter_name} invited you to join “{info.silo_name}”
            </h1>
            <p className="text-sm text-[#6B6A68] mt-2 leading-relaxed">
              You'll get the shared family organizer — the capture pad, the calendar,
              chores and pings — right from your own account.
            </p>
            {info.is_child && (
              <div className="mt-4 bg-[#F8F1E2] border border-[#E5C77F] rounded-lg p-4" data-testid="join-family-child-panel">
                <div className="flex items-center gap-2 text-[#8A5A00] text-sm font-medium">
                  <ShieldWarning size={16} weight="duotone" /> Family-restricted membership
                </div>
                <p className="text-[12px] text-[#6B6A68] mt-1.5 leading-relaxed">
                  This invite marks your account as a <strong>family member with purchase guardrails</strong>:
                  every checkout requires typing a card fresh — no saved cards, ever. Nothing else changes.
                  A parent can release this anytime, and leaving the household removes it automatically.
                </p>
              </div>
            )}
            <div className="mt-6 space-y-2">
              {authed ? (
                <button onClick={join} disabled={joining} data-testid="join-family-join-btn"
                  className="w-full text-sm px-4 py-2.5 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50">
                  {joining ? "Joining…" : info.is_child ? "Join household — I understand the restriction" : "Join household"}
                </button>
              ) : (
                <>
                  <button onClick={() => goAuth("/signup")} data-testid="join-family-signup-btn"
                    className="w-full text-sm px-4 py-2.5 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E]">
                    Sign up to join
                  </button>
                  <button onClick={() => goAuth("/login")} data-testid="join-family-login-btn"
                    className="w-full text-sm px-4 py-2.5 rounded-md border hairline text-[#1C1B1A] hover:border-[#4A5D4E]">
                    I already have an account — log in
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
